import React, { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import UserHeader from "@/components/home/UserHeader";
import HeroSection from "@/components/home/HeroSection";
import FeatureCards from "@/components/home/FeatureCards";
import { Separator } from "@/components/ui/separator";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { User, FileText } from "lucide-react";
import LoginPromptDialog from "@/components/home/LoginPromptDialog";
import { authService } from "@/services/auth.service";
import { sessionManager } from "@/utils/sessionManager";
import { useFeedbackPopup } from "@/hooks/useFeedbackPopup";
import { FeedbackPopup } from "@/components/FeedbackPopup";

const Index = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [showTherapistDialog, setShowTherapistDialog] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const { 
    showFeedbackPopup, 
    setShowFeedbackPopup, 
    submitFeedback 
  } = useFeedbackPopup(user);

  useEffect(() => {
    const checkUser = async () => {
      try {
        const {
          data
        } = await supabase.auth.getUser();
        setUser(data.user);
        if (data.user) {
          const {
            data: roleData,
            error: roleError
          } = await supabase.from('user_roles').select('role').eq('user_id', data.user.id).maybeSingle();
          if (roleData) {
            setUserRole(roleData.role);
          } else {
            console.log("User has no role - redirecting to auth");
            await sessionManager.performSilentLogout();
            setUser(null);
            navigate('/auth');
          }
        } else {
          // If no user is found, redirect to auth page
          navigate('/auth');
        }
      } catch (error) {
        console.error("Error checking user:", error);
        await sessionManager.performSilentLogout();
        navigate('/auth');
      } finally {
        setLoading(false);
      }
    };
    checkUser();
    const {
      data: authListener
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        try {
          const {
            data: roleData,
            error: roleError
          } = await supabase.from('user_roles').select('role').eq('user_id', session.user.id).maybeSingle();
          if (roleData) {
            setUserRole(roleData.role);
          } else {
            console.log("User has no role from auth change - redirecting to auth");
            await sessionManager.performSilentLogout();
            setUser(null);
            setUserRole(null);
            navigate('/auth');
          }
        } catch (error) {
          console.error("Error checking role on auth change:", error);
          await sessionManager.performSilentLogout();
          setUser(null);
          setUserRole(null);
          navigate('/auth');
        }
      } else {
        setUserRole(null);
        // If user logs out, redirect to auth page
        navigate('/auth');
      }
      setLoading(false);
    });
    const timeoutId = setTimeout(() => {
      if (loading) {
        setLoading(false);
        console.log("Loading state was forcibly cleared after timeout");
      }
    }, 5000);
    return () => {
      if (authListener && authListener.subscription) {
        authListener.subscription.unsubscribe();
      }
      clearTimeout(timeoutId);
    };
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await authService.signOut();
      setUserRole(null);
      navigate('/auth');
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  const handleTherapistsListClick = () => {
    if (!user) {
      localStorage.setItem('redirectAfterLogin', '/therapists');
      setShowLoginPrompt(true);
    } else {
      navigate('/therapists');
    }
  };

  const handleJournalClick = () => {
    navigate('/journal');
  };

  const handleTherapistClick = () => {
    navigate('/therapist');
  };

  const handleTherapistLogin = () => {
    navigate('/therapist-login');
  };

  // If still loading, show a loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // If no user is authenticated, redirect to auth page
  if (!user) {
    return null; // This will be handled by the useEffect redirect
  }

  return (
    <div className="min-h-screen bg-background">
      <main className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h1 className="text-2xl font-bold mb-4">Welcome to Your Dashboard</h1>
          <p className="text-gray-600 mb-4">You are logged in as: {user?.email}</p>
          <p className="text-blue-600 font-semibold">TEST MESSAGE - If you see this, the page is updating correctly!</p>
          <button
            onClick={handleLogout}
            className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
          >
            Logout
          </button>
        </div>
        <UserHeader user={user} loading={loading} handleLogout={handleLogout} />
        <HeroSection user={user} userRole={userRole} loading={loading} setShowTherapistDialog={setShowTherapistDialog} setShowLoginPrompt={setShowLoginPrompt} handleTherapistsListClick={handleTherapistsListClick} handleJournalClick={handleJournalClick} handleTherapistClick={handleTherapistClick} />
        <FeatureCards />
      </main>
      
      <Separator className="my-8 bg-gray-200" />
      
      <footer className="container mx-auto px-4 py-6">
        <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <div className="max-w-md">
            <p className="text-dark gray-600 text-sm">Clou</p>
          </div>
          
          <div className="max-w-md text-center">
            <p className="text font-bold text-lg text-gray-600 text-center">REFLECT. RECORD. RECONNECT</p>
          </div>
          
          <div className="max-w-md text-right">
            <Link to="/privacy-policy" className="text-gray-600 hover:text-gray-800 text-sm">Privacy Policy</Link>
          </div>
        </div>
      </footer>
      
      {showLoginPrompt && (
        <LoginPromptDialog 
          open={showLoginPrompt} 
          onOpenChange={setShowLoginPrompt} 
        />
      )}
      
      {showFeedbackPopup && (
        <FeedbackPopup
          open={showFeedbackPopup}
          onOpenChange={setShowFeedbackPopup}
          onSubmit={submitFeedback}
        />
      )}
      <div style={{ position: 'fixed', bottom: 12, right: 16, zIndex: 50 }} className="text-xs text-gray-400 select-none pointer-events-none">
        V1.0.001
      </div>
    </div>
  );
};

export default Index;
